import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/site/Logo";

const links = [
  { href: "#problem", label: "Why It Matters" },
  { href: "#services", label: "What You Get" },
  { href: "#process", label: "Process" },
  { href: "#industries", label: "Industries" },
  { href: "#portfolio", label: "Examples" },
  { href: "#faq", label: "FAQ" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-gray-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="#top"
            data-testid="nav-brand"
            className="flex items-center"
          >
            <Logo variant="icon" height={40} />
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <button
                key={l.href}
                onClick={() => scrollTo(l.href)}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Button
              data-testid="nav-cta-secondary"
              variant="ghost"
              onClick={() => scrollTo("#demo")}
              className="text-gray-900 hover:bg-gray-100"
            >
              Talk to us
            </Button>
            <Button
              data-testid="nav-cta-primary"
              onClick={() => scrollTo("#demo")}
              className="bg-blue-700 hover:bg-blue-800 text-white rounded-md"
            >
              Get Free Demo
            </Button>
          </div>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-gray-200 bg-white"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden bg-white border-t border-gray-200"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-3">
              {links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => scrollTo(l.href)}
                  className="text-left text-base text-gray-800 py-2"
                >
                  {l.label}
                </button>
              ))}
              <Button
                onClick={() => scrollTo("#demo")}
                className="bg-blue-700 hover:bg-blue-800 text-white mt-2"
                data-testid="nav-mobile-cta"
              >
                Get Free Demo
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
